import { useEffect, useState } from 'react';
import usePrinterNative from './usePrinterNative';
import { Capacitor } from '@capacitor/core';
import { usePrinterWeb } from './usePrinterWeb';

type PrinterMode = 'web' | 'native';

const usePrinter = (): any => {
  const [mode, setMode] = useState<PrinterMode>('native');
  const webPrinter: any = usePrinterWeb();
  const nativePrinter = usePrinterNative();

  useEffect(() => {
    const savedMode = localStorage.getItem('printer_mode') as PrinterMode | null;
    if (savedMode === 'web' || savedMode === 'native') {
      setMode(savedMode);
      return;
    }

    let platform = 'web';
    try {
      platform = Capacitor?.getPlatform ? Capacitor.getPlatform() : (window as any)?.Capacitor?.getPlatform?.() ?? 'web';
    } catch (e) {
      platform = 'web';
    }

    const defaultMode: PrinterMode = platform === 'web' ? 'web' : 'native';
    setMode(defaultMode);
    localStorage.setItem('printer_mode', defaultMode);
  }, []);

  const smartAddToQueue = async (order: any) => {
    const payload =
      order && typeof order === 'object' && ('data' in order || 'ip_printer' in order || 'type' in order || 'dataSettings' in order || 'stadium' in order)
        ? order
        : { data: order };

    if (mode === 'web') {
      if (webPrinter && typeof webPrinter.addToQueue === 'function') {
        return await webPrinter.addToQueue(payload);
      }
      throw new Error('Web printer not available');
    }

    if (nativePrinter && typeof nativePrinter.addToQueue === 'function') {
      return await nativePrinter.addToQueue(payload);
    }

    throw new Error('Native printer not available');
  };

  if (mode === 'web') {
    return {
      ...webPrinter,
      mode: 'web' as const,
      printerTarget: webPrinter.printerTarget,
      isConnected: webPrinter.isConnected,
      isConnecting: webPrinter.isConnecting,
      bluetoothEnabled: true,
      addToQueue: smartAddToQueue,
      connectPrinter: webPrinter.connectPrinter,
      disconnectPrinter: webPrinter.disconnectPrinter,
      removePrinter: webPrinter.removePrinter,
      scanForPrinters: webPrinter.scanForPrinters,
      isScanning: webPrinter.isScanning,
      isPrinting: webPrinter.isPrinting,
      queueLength: webPrinter.queueLength,
    };
  }

  return {
    ...nativePrinter,
    mode: 'native' as const,
    addToQueue: smartAddToQueue,
  };
};

export default usePrinter;
